/* === AgCheckbox — Anagogia Design System === */

import { useEffect, useRef } from 'react';
import styles from './AgCheckbox.module.css';
import { Check, Minus } from 'lucide-react';

interface AgCheckboxProps {
  /** Controlled checked state */
  checked?: boolean;
  /** Uncontrolled default */
  defaultChecked?: boolean;
  /** Indeterminate state (visual only, does not affect checked) */
  indeterminate?: boolean;
  /** Called with the new checked value */
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: 'sm' | 'md';
  color?: 'primary' | 'secondary' | 'error';
  disabled?: boolean;
  /** Optional label rendered next to the checkbox */
  label?: React.ReactNode;
  /** Accessible name when no visible `label` is rendered */
  'aria-label'?: string;
  className?: string;
}

const COLOR_MAP: Record<string, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  error: styles.colorError,
};

/**
 * Case à cocher avec label.
 * @category Formulaires
 */
function AgCheckbox({
  checked,
  defaultChecked,
  indeterminate = false,
  onChange,
  size = 'md',
  color = 'primary',
  disabled = false,
  label,
  'aria-label': ariaLabel,
  className,
}: AgCheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // L'état indéterminé n'existe qu'en propriété DOM (pas d'attribut HTML) :
  // on le pose ici pour que les technologies d'assistance le voient aussi.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.checked, e);
  };

  const wrapperCls = [
    styles.wrapper,
    disabled ? styles.disabled : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const boxCls = [styles.box, styles[size], COLOR_MAP[color]]
    .filter(Boolean)
    .join(' ');

  const iconSize = size === 'sm' ? 14 : 18;

  return (
    <label className={wrapperCls}>
      <span className={styles.checkboxContainer}>
        <input
          ref={inputRef}
          type="checkbox"
          className={styles.input}
          aria-label={ariaLabel}
          aria-checked={indeterminate ? 'mixed' : undefined}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={handleChange}
          disabled={disabled}
        />
        <span className={boxCls} aria-hidden="true">
          {indeterminate ? (
            <Minus size={iconSize} className={styles.icon} />
          ) : (
            <Check size={iconSize} className={styles.icon} />
          )}
        </span>
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
}

export { AgCheckbox };
export type { AgCheckboxProps };
export default AgCheckbox;
